import { plainToClass } from "class-transformer";
import { DateUpdatedSection } from "../../../../shared/components/date-updated-section/date-updated-section.view";
import { Site } from "../../../../shared/entities/scripts/site";
import { Study } from "../../../../shared/entities/scripts/study";
import { SortDates } from "../../../../shared/scripts/date-functions";
import { IStudySiteFilterParent, StudySiteFilterSection } from "../../../../shared/scripts/filtering/filtering.study-site";
import { ForecastGraphDataPoint } from "../../forcasts/scripts/forecasts.entities";
import { SiteInfo } from "../../sites/shared/sites.entities";
import { ChartController, ChartSetup, ChartSeries, ChartDataPoint, CategoryChartSeries } from "./enrollment.chart-controller";
import { AggregatedParticipants, Participant } from "./enrollment.entities";
import { EnrollmentGauges } from "./enrollment.guages";
import { ProgressBarSection } from "./enrollment.progress-bars";
import { DoArraysOverlap, Unique } from "../../../../shared/scripts/array-functions";
import { FormatNumberWithCommas } from "../../../../shared/scripts/number-functions";
import { ResetDropdownFilters } from "../../../../shared/scripts/filtering/filtering.utilities";
import { DrilldownChartBar } from './enrollment.drilldown-bar-chart';
import { ChartBar } from "./enrollment.chart-bar";
import { ChartLine } from "./enrollment.chart-line";
import { EnrollmentLookup } from "./enrollment.lookup";


export interface IEnrollmentPageData {
    allStudies: Study[];

    consentedSites: Site[];
    activatedSites: Site[];
    sitesActivatedByMonthByStudy: Map<string, number>; //key = studyId_dateTimestamp, value = count

    selectedParticipants: Participant[];
    aggregatedParticipants: AggregatedParticipants;

    forecastRandomized: ForecastGraphDataPoint[];
    selectedForecastRandomized: ForecastGraphDataPoint[];

    selectedPopulation: string; //consented, randomized, screen-fail
    selectedBreakdown: string; // "Overall", "Race", "Age", etc...
    selectedAggregation: string; // overall, ByStudy, BySite
    selectedGraphView: string; //cumulative (line), pie chart, by month (bar), rate by month (bar)
    showRandomized: boolean;

}

export class EnrollmentPage implements IStudySiteFilterParent, IEnrollmentPageData {

    allStudies: Study[];

    consentedSites: Site[];
    activatedSites: Site[];
    sitesActivatedByMonthByStudy: Map<string, number> = new Map();

    allParticipants: Participant[];
    selectedParticipants: Participant[];
    aggregatedParticipants: AggregatedParticipants = new AggregatedParticipants();
    aggParticipantsByStudy: Map<number, AggregatedParticipants> = new Map<number, AggregatedParticipants>();
    aggParticipantsByAppendix: Map<number, AggregatedParticipants> = new Map<number, AggregatedParticipants>();
    aggParticipantsBySite: Map<number, AggregatedParticipants> = new Map<number, AggregatedParticipants>(); 


    forecastRandomized: ForecastGraphDataPoint[];
    selectedForecastRandomized: ForecastGraphDataPoint[];


    filter: StudySiteFilterSection;
    progressBars: ProgressBarSection;
    gauges: EnrollmentGauges;
    chartController: ChartController;

    selectedPopulation: string;
    selectedBreakdown: string;
    selectedAggregation: string;
    selectedGraphView: string;
    showRandomized: boolean;

    totEnrollDrilldownChart: DrilldownChartBar = new DrilldownChartBar();
    totEnrollSortStr: string = "highest";

    constructor(siteInfoJson: any, participantJson: any, randomizedAspirationalJson: any, lastUpdatedJson: any) {
        $(() => {

            this.showRandomized = true;

            if (this.allStudies == null || this.allStudies == undefined) {
                this.allStudies = [];
            }

            let foundSiteIds: string[] = [];
            let foundStudyIds: number[] = [];

            this.allParticipants = plainToClass(Participant, <Participant[]>participantJson);
            this.allParticipants = this.allParticipants.filter((p: Participant) => {
                p.initialize();

                if (p.Consented == 1) {
                    if (!foundSiteIds.includes(p.SiteNumber + "-" + p.StudyID)) { foundSiteIds.push(p.SiteNumber + "-" + p.StudyID); }

                    if (!foundStudyIds.includes(p.StudyID)) {
                        this.allStudies.push(new Study(p.StudyID, Study.GetStudyName(p.StudyID)));
                        foundStudyIds.push(p.StudyID);
                    }

                    return true;
                }
                return false;
            });

            let siteInfo: SiteInfo[] = SiteInfo.Parse(siteInfoJson);
            let consentedSiteMap: Map<number, Site> = new Map<number, Site>();
            let activatedSiteMap: Map<number, Site> = new Map<number, Site>();
            siteInfo.forEach((s: SiteInfo) => {

                foundSiteIds.forEach((id: string) => {
                    let siteId =  Number.parseInt(id.split('-')[0]);
                    let studyId = Number.parseInt(id.split('-')[1]);

                    if (siteId == s.SiteId) {
                        if (consentedSiteMap.has(s.SiteId)) {
                            consentedSiteMap.get(s.SiteId).StudyIds.push(studyId);
                        }
                        else {
                            let newSite: Site = new Site(s.SiteId, s.getSiteNameNumber());
                            newSite.StudyIds.push(studyId);
                            consentedSiteMap.set(s.SiteId, newSite);
                        }
                    }
                });

                //TODO: Remove RVLC study ID hard coding once we have a better way to identify which sites are activated for which studies in the source data
                let studyId = 9 //RVLC
                if (s.ActivationDate != undefined) {
                    s.setActivationTimestamp();
                    if (activatedSiteMap.has(s.SiteId)) {
                        activatedSiteMap.get(s.SiteId).StudyIds.push(studyId);
                        activatedSiteMap.get(s.SiteId).ActivationTimestamps[studyId] = s.ActivationTimestamp;
                    }
                    else {
                        let newSite: Site = new Site(s.SiteId, s.getSiteNameNumber());
                        newSite.StudyIds.push(studyId);
                        newSite.ActivationTimestamps[studyId] = s.ActivationTimestamp;
                        activatedSiteMap.set(s.SiteId, newSite);
                    }
                } 
            });

            this.consentedSites = Array.from(consentedSiteMap.values()).sort((a: Site, b: Site) => a.Name.localeCompare(b.Name));
            this.activatedSites = Array.from(activatedSiteMap.values()).sort((a: Site, b: Site) => a.Name.localeCompare(b.Name));

            this.forecastRandomized = plainToClass(ForecastGraphDataPoint, <ForecastGraphDataPoint[]>randomizedAspirationalJson);
            this.forecastRandomized = this.forecastRandomized.map((point: ForecastGraphDataPoint) => {
                point.initialize("first", "count");
                return point;
            }).filter((x: ForecastGraphDataPoint) => x.TypeId == 2);
            this.selectedForecastRandomized = this.forecastRandomized;


            new DateUpdatedSection(lastUpdatedJson);

            this.changeSelectedPopulation("randomized", false);
            this.changeSelectedBreakdown("All Participants", '', false);
            this.changeSelectedAggregation("Overall", false);
            this.changeSelectedGraphView("Cumulative", false);

            this.progressBars = new ProgressBarSection();
            this.gauges = new EnrollmentGauges(this);
            this.chartController = new ChartController(this);

            this.filter = new StudySiteFilterSection("#study-dropdown",  "#site-dropdown", this.allStudies, this.consentedSites, this);


            $('#loading-indicator').hide();
            $('.loaded-content').removeClass('invisible');
            this.hideRandomizedToggleButton();

            this.initTotalEnrollmentModal();
            this.initCumulativeModal();
        });
    }

    changeSelectedPopulation(value: string, triggerRefresh: boolean = true): void {
        this.selectedPopulation = value;

        //need to change sorting for graphing
        if (value == "consented") {
            this.allParticipants = this.allParticipants.sort((p1, p2) => SortDates(p1.DateConsented, p2.DateConsented));
            $('#label-population').text("Consented");
            this.showRandomizedToggleButton();
        }
        else if (value == "randomized") {
            this.allParticipants = this.allParticipants.sort((p1, p2) => SortDates(p1.DateRandomized, p2.DateRandomized));
            $('#label-population').text("Randomized");
            this.hideRandomizedToggleButton();
        }
        else if (value == "screen-failed") {
            this.allParticipants = this.allParticipants.sort((p1, p2) => SortDates(p1.DateConsented, p2.DateConsented));
            $('#label-population').text("Screen Failed");
            this.hideRandomizedToggleButton();
        }


        if (triggerRefresh) { this.filter?.refreshFilters(); }
    }

    changeSelectedBreakdown(newBreakdown: string, selectedDomainId: string = "", triggerRefresh: boolean = true): void {
        this.selectedBreakdown = newBreakdown;

        $('#chart-view-label').text((newBreakdown == "All Participants" ? newBreakdown : ("By " + newBreakdown)));
        $('.chart-details-toggle-button').removeClass('selected');

        let activateStudies: boolean = true;
        let activateSites: boolean = true;

        if (this.selectedBreakdown == "All Participants") {
            $('#view-all-no-breakdown').hide();

            if (this.selectedGraphView == "RatePerSiteByMonth") { activateSites = false; }
            this.enableSelectionOption('#enrollment-graph-view', "RatePerSiteByMonth", true);
        }
        else {
            $('#view-all-no-breakdown').show();
            $('#' + selectedDomainId).addClass('selected');

            if (this.selectedGraphView != "Cumulative" && this.selectedGraphView != "Proportional") {
                activateStudies = activateSites = false;
            }

            if (this.selectedAggregation != "Overall") { this.changeSelectedAggregation("Overall", false); }
            if (this.selectedGraphView == "RatePerSiteByMonth") { $('#enrollment-graph-view').val("Cumulative").trigger('change'); }
            this.enableSelectionOption('#enrollment-graph-view', "RatePerSiteByMonth", false);
        }
        this.enableButton('#view-studies-button', activateStudies);
        this.enableButton('#view-sites-button', activateSites);

        if (triggerRefresh) { this.chartController.UpdateData(); }
    }

    changeSelectedAggregation(newAggregation: string, triggerRefresh: boolean = true): void {
        this.selectedAggregation = newAggregation;

        $('.aggregation-option').removeClass('active');
        if (this.selectedAggregation == "Overall") { $('#view-overall-button').addClass('active'); }
        if (this.selectedAggregation == "ByStudy") { $('#view-studies-button').addClass('active'); }
        if (this.selectedAggregation == "BySite") { $('#view-sites-button').addClass('active'); }

        if (triggerRefresh) { this.chartController.UpdateData(); }


        if (this.selectedAggregation != "Overall" && this.selectedBreakdown != "All Participants" && this.selectedGraphView == "Proportional") {
            this.showTotalEnrollmentModal();
        } else if (this.selectedAggregation != "Overall" && this.selectedBreakdown != "All Participants" && this.selectedGraphView == "Cumulative") {
            this.showCumulativeModal();
        }
    }

    changeSelectedGraphView(newGraphView: string, triggerRefresh: boolean = true): void {
        this.selectedGraphView = newGraphView;


        if (this.selectedGraphView == "Cumulative" || this.selectedGraphView == "ByMonth" || this.selectedGraphView == "RatePerSiteByMonth") {
            let monthField: string = "";
            let countField: string = "";
            if (this.selectedGraphView == "Cumulative") { monthField = "last"; countField = "count"; }
            else if (this.selectedGraphView == "ByMonth") { monthField = "first"; countField = "count"; }
            else if (this.selectedGraphView == "RatePerSiteByMonth") { monthField = "first"; countField = "rate"; }

            this.forecastRandomized.forEach((x: ForecastGraphDataPoint) => { x.DayOfMonth = monthField; x.CountField = countField; });
            this.selectedForecastRandomized.forEach((x: ForecastGraphDataPoint) => { x.DayOfMonth = monthField; x.CountField = countField; });
        }
        else if (this.selectedGraphView == "RatePerSiteByMonth") {
            if (this.selectedAggregation == "BySite") { this.changeSelectedAggregation("Overall", false); }
            this.enableButton('#view-sites-button', false);
        }
        else if (this.selectedBreakdown == "All Participants") { this.enableButton('#view-sites-button', true); }


        if (this.selectedGraphView != "Cumulative" && this.selectedGraphView != "Proportional" && this.selectedBreakdown != "All Participants") {
            this.enableButton('#view-studies-button', false);
            this.enableButton('#view-appendices-button', false);
            this.enableButton('#view-sites-button', false);
        } else {
            this.enableButton('#view-studies-button', true);
            this.enableButton('#view-appendices-button', true);
            this.enableButton('#view-sites-button', true);
        }


        if (triggerRefresh) { this.chartController.UpdateData(); }
    }


    toggleRandomized(): void {
        this.showRandomized = !this.showRandomized;
        this.FilterUpdated(this.filter.selectedStudyIds, this.filter.selectedSiteIds);
    }


    FilterUpdated(studyIds: number[], siteIds: number[]) {

        this.selectedParticipants = this.allParticipants.filter((p: Participant) => {
            if (this.selectedPopulation == "consented" && (p.Consented != 1 || (!this.showRandomized && p.Randomized == 1))) return false;
            else if (this.selectedPopulation == "randomized" && p.Randomized != 1) return false;
            else if (this.selectedPopulation == "screen-failed" && p.ScreenFail != 1) return false;

            if(!studyIds.includes(p.StudyID)) return false;
            if (!siteIds.includes(p.SiteNumber)) return false;

            return true;
        });

        let monthTimestamps: number[] = [];
        this.selectedParticipants.forEach((p: Participant) => {
            if (this.selectedPopulation == "consented" || this.selectedPopulation == "screen-failed") {
                if (!monthTimestamps.includes(p.MonthConsented.getTime())) { monthTimestamps.push(p.MonthConsented.getTime()); }
            }
            else if (this.selectedPopulation == "randomized") {
                if (!monthTimestamps.includes(p.MonthRandomized.getTime())) { monthTimestamps.push(p.MonthRandomized.getTime()); }
            }
        });


        this.sitesActivatedByMonthByStudy.clear();
        let subsetOfSites: boolean = !siteIds.includes(0);

        this.activatedSites.forEach((s: Site) => {
            if (!DoArraysOverlap(s.StudyIds, studyIds)) return;
            if (subsetOfSites && !siteIds.includes(s.Id)) return;

            studyIds.forEach((studyId: number) => {
                monthTimestamps.forEach((monthTimestamp: number) => {
                    if (s.ActivationTimestamps[studyId] != undefined && s.ActivationTimestamps[studyId] <= monthTimestamp) {
                        let key: string = studyId + "_" + monthTimestamp;
                        let count: number = 0;
                        if (this.sitesActivatedByMonthByStudy.has(key)) {
                            count = this.sitesActivatedByMonthByStudy.get(key);
                        }
                        count += 1;
                        this.sitesActivatedByMonthByStudy.set(key, count);
                    }
                });
            });
        });

        //forecasted information
        if (this.selectedPopulation == "randomized") {
            if (!siteIds.includes(0)) { this.selectedForecastRandomized = []; }
            else {
                this.selectedForecastRandomized = this.forecastRandomized.filter((x: ForecastGraphDataPoint) => studyIds.includes(x.PrimeId));
            }
        }
        else { this.selectedForecastRandomized = []; }

        this.aggregatedParticipants.aggregate(this.selectedParticipants);

        this.progressBars.updateProgressBarData(this.aggregatedParticipants);




        $('#value-total-population').text(FormatNumberWithCommas(String(this.selectedParticipants.length)));



        this.chartController.UpdateData();
    }

    resetFilters(): void {
        this.showRandomized = false;
        (document.getElementById("randomized-switch") as HTMLInputElement).checked = false;
        $('#population-select').val("randomized");
        $('#enrollment-graph-view').val("Cumulative");

        this.changeSelectedPopulation("randomized", true);
        this.changeSelectedBreakdown("All Participants", '', true);
        this.changeSelectedAggregation("Overall", true);

        ResetDropdownFilters(['#study-dropdown', '#site-dropdown']);

        this.changeSelectedGraphView('Cumulative', true);
    }

    private enableButton(buttonId: string, active: boolean): void {
        $(buttonId).prop("disabled", !active);
        if (!active) { $(buttonId).addClass("disabled"); }
        else { $(buttonId).removeClass("disabled"); }
    }

    private enableSelectionOption(selectId: string, value: string, active: boolean): void {
        $(selectId + ' option').each(function () {
            if ($(this).val() == value) {
                if (active) { $(this).removeAttr('disabled'); }
                else { $(this).attr('disabled', 'disabled'); }
                return;
            }
        });

        $(selectId).selectpicker('refresh');
    }

    showRandomizedToggleButton(): void {
        $('#randomized-toggle').show();
        $('#reset-filters').removeClass("ml-auto");
    }

    hideRandomizedToggleButton(): void {
        $('#randomized-toggle').hide();
        $('#reset-filters').addClass("ml-auto");
    }

    reloadTotalEnrollmentChart(): void {
        $("#total-enrollment-modal-subheading").empty();
        $("#total-enrollment-modal-subheading").append(this.selectedBreakdown + " Breakdown By " + this.selectedAggregation.slice(2));

        $("#total-enrollment-modal-container").empty();
        $("#total-enrollment-modal-container").append("<div id='total-enrollment-modal-chart' > </div>");

        let chartSeries: CategoryChartSeries[] = [];
        let xTitle: string = "";
        let yTitle: string = "";
        let xLabelsMap: Map<number, string> = new Map<number, string>();
        let xLabels: string[] = [];
        let sortedMappedAggregatedData: [number, AggregatedParticipants][] = [];


        if (this.selectedAggregation == "ByStudy") {
            this.allStudies.forEach((study: Study) => {
                let aggregatedData: AggregatedParticipants = this.aggParticipantsByStudy.get(study.Id);
                if (!aggregatedData) { aggregatedData = new AggregatedParticipants(); }
                aggregatedData.aggregate(this.selectedParticipants.filter((p: Participant) => p.StudyID == study.Id));
                sortedMappedAggregatedData.push([study.Id, aggregatedData]);
                xLabelsMap.set(study.Id, study.Name);
            });
            xTitle = "Study";
        } else if (this.selectedAggregation == "BySite") {
            this.consentedSites.forEach((site: Site) => {
                let aggregatedData: AggregatedParticipants = this.aggParticipantsBySite.get(site.Id);
                if (!aggregatedData) { aggregatedData = new AggregatedParticipants(); }
                aggregatedData.aggregate(this.selectedParticipants.filter((p: Participant) => p.SiteNumber == site.Id));
                sortedMappedAggregatedData.push([site.Id, aggregatedData]);
                xLabelsMap.set(site.Id, site.Name);
            });
            xTitle = "Site";
        }


        sortedMappedAggregatedData.sort((a: [number, AggregatedParticipants], b: [number, AggregatedParticipants]) => {
            switch (this.totEnrollSortStr) {
                case "highest":
                    return a[1].Count > b[1].Count ? -1 : 1;
                case "lowest":
                    return a[1].Count < b[1].Count ? -1 : 1;
                case "alphabetical":
                    return xLabelsMap.get(a[0]).localeCompare(xLabelsMap.get(b[0]));
                case "alpha-reverse":
                    return xLabelsMap.get(b[0]).localeCompare(xLabelsMap.get(a[0]));
            }


        });


        let indices = this.chartController.getIndices();

        sortedMappedAggregatedData.forEach(([id, aggregatedData]: [number, AggregatedParticipants]) => {
            xLabels.push(xLabelsMap.get(id));

            let data: number[] = [];
            if (this.selectedBreakdown == "Age") { data = aggregatedData.Age; }
            else if (this.selectedBreakdown == "Race") { data = aggregatedData.Race; }
            else if (this.selectedBreakdown == "Ethnicity") { data = aggregatedData.Hispanic; }
            else if (this.selectedBreakdown == "Hispanic Or Non-White") { data = aggregatedData.NonWhiteHispanic; }
            else if (this.selectedBreakdown == "Sex") { data = aggregatedData.Sex; }

            indices.forEach((index: number) => {
                let label: string = "";
                if (this.selectedBreakdown == "Ethnicity" || this.selectedBreakdown == "Hispanic Or Non-White") {
                    label = EnrollmentLookup.GetYesNo(index);
                    if (this.selectedBreakdown == "Ethnicity" && label != "Unknown") {
                        label = (label == "Yes") ? "Hispanic" : "Not Hispanic";
                    }
                }
                else if (this.selectedBreakdown == "Sex") {
                    label = EnrollmentLookup.GetSex(index);
                }
                else if (this.selectedBreakdown == "Age") {
                    label = EnrollmentLookup.GetAgeGroup(index);
                }
                else if (this.selectedBreakdown == "Race") {
                    label = EnrollmentLookup.GetRace(index);
                }

                let series = new CategoryChartSeries();
                if (chartSeries.filter((s: CategoryChartSeries) => s.name == label).length > 0) {
                    series = chartSeries.filter((s: CategoryChartSeries) => s.name == label)[0];
                } else {
                    series.data = [];
                    series.type = 'bar';
                    series.name = label;
                    series.color = this.chartController.getBreakdownColor(index);
                    chartSeries.push(series);
                }

                series.data.push(data[index]);

            });


        });

        $('#total-enrollment-modal-chart').height(sortedMappedAggregatedData.length * 50 + 100);

        let setup = new ChartSetup(chartSeries, false, false, true, true);

        this.totEnrollDrilldownChart.UpdateChart(setup, xTitle, yTitle, xLabels);
    }

    initTotalEnrollmentModal(): void {

        $("#total-enrollment-modal-container").empty();
        $("#total-enrollment-modal-container").append("<div class='col-12'><h6>Loading....</h6></div>")

        $("#total-enrollment-modal").on("shown.bs.modal", () => {
            this.reloadTotalEnrollmentChart();
        });


        $("#total-enrollment-modal").on("hide.bs.modal", () => {
            $("#total-enrollment-modal-container").empty();
            $("#total-enrollment-modal-container").append("<div class='col-12'><h6>Loading....</h6></div>")
            this.changeSelectedAggregation("Overall");
        });
    }

    showTotalEnrollmentModal(): void {
        $("#total-enrollment-modal").modal('show');
    }

    setTotalEnrollmentSort(sortBy: string): void {
        this.totEnrollSortStr = sortBy;
        this.reloadTotalEnrollmentChart();
    }


    initCumulativeModal(): void {

        $("#cumulative-modal-container").empty();
        $("#cumulative-modal-container").append("<div class='col-12'><h6>Loading....</h6></div>")

        $("#cumulative-modal").on("shown.bs.modal", () => {

            $("#cumulative-modal-subheading").empty();
            $("#cumulative-modal-subheading").append(this.selectedBreakdown + " Breakdown By " + this.selectedAggregation.slice(2));


            $("#cumulative-modal-container").empty();

            let chartBuilder: ChartLine = new ChartLine();

            

            var aggregatedCatMap: Map<number, string> = new Map<number, string>();
            var selectorFunc: (p: Participant) => boolean = null;
            var selectedIds: number[] = []
            var currID: number = 0;
            if (this.selectedAggregation == "ByStudy") {
                this.allStudies.forEach((study: Study) => { aggregatedCatMap.set(study.Id, study.Name); });
                selectorFunc = (p: Participant) => p.StudyID == currID;
                selectedIds = this.filter.selectedStudyIds;
            } else if (this.selectedAggregation == "BySite") {
                this.consentedSites.forEach((site: Site) => { aggregatedCatMap.set(site.Id, site.Name); });
                selectorFunc = (p: Participant) => p.SiteNumber == currID;
                selectedIds = this.filter.selectedSiteIds;
            }

            let charts: [id: number, setup: ChartSetup, total: number][] = [];
            aggregatedCatMap.forEach((name: string, id: number) => {
                if (selectedIds.filter(x => x == id).length == 0) {
                    return
                }
                currID = id;
                let filteredParticipants = this.selectedParticipants.filter(selectorFunc);
                let chartSetup: ChartSetup = this.chartController.createSeriesForOverall(filteredParticipants);

                let total: number = 0;
                chartSetup.series.forEach((s: ChartSeries) => {
                    if (s.data[s.data.length - 1]) {
                        total += s.data[s.data.length - 1].y;
                    }
                });

                charts.push([id, chartSetup, total]);
            });

            charts.sort((a, b) => b[2] - a[2]);

            charts.forEach((c: [id: number, setup: ChartSetup, total: number]) => {
                $("#cumulative-modal-container").append("<div id='cumulative-modal-chart-" + c[0] + "' class='col-6'></div>")
                chartBuilder.UpdateChart(c[1], aggregatedCatMap.get(c[0]) + " - Total: " + c[2], "cumulative-modal-chart-" + c[0]);
            });
        });

        $("#cumulative-modal").on("hide.bs.modal", () => {
            $("#cumulative-modal-container").empty();
            $("#cumulative-modal-container").append("<div class='col-12'><h6>Loading....</h6></div>")
            this.changeSelectedAggregation("Overall");
        });
    }

    showCumulativeModal(): void {
        $("#cumulative-modal").modal('show');
    }

}